import React, { useState } from 'react';
import styled from 'styled-components';
import { Box, BreadcrumbItem } from '@drivekyte/web-components';
import Link from '../../../link';
import Header from './components/header';
import Logo from '../logo';
import { useRouter } from 'next/router';
import useAuthentication from '@/hooks/use-authentication';
import useMe from '@/hooks/use-me';
import useSignOut from '@/hooks/use-sign-out';
import useTranslation from '@/hooks/use-translation';
import useAnalyticsWithSource from '@/hooks/use-analytics-with-source';
import { FAQ_URL } from '@/config/global-constants';
import { Typography } from '@drivekyte/ui';

export type NavigationProps = {
  searchConfig: {
    showSearchToggler: boolean;
    showSearchInput: boolean;
  };
  subscriptionBreadcrumbs?: BreadcrumbItem[];
};

const Navigation = ({
  searchConfig,
  subscriptionBreadcrumbs,
}: NavigationProps) => {
  const router = useRouter();
  const { t } = useTranslation('components.header');
  const { isAuthenticated } = useMe();
  const signOutMutation = useSignOut();
  const { showSearchInput, showSearchToggler } = searchConfig;
  const [showSearchButton, setShowSearchButton] =
    useState<boolean>(showSearchInput);
  const handleAuth = useAuthentication();
  const searchEnabled = showSearchInput || showSearchToggler;
  const { trackEvent, AnalyticsEvents } = useAnalyticsWithSource();

  const handlePressSignOut = (event: React.MouseEvent) => {
    event.preventDefault();
    trackEvent(AnalyticsEvents.Navigation.NavSignOutButtonClicked);
    signOutMutation.mutate();
  };

  const handleSignUp = (event: React.MouseEvent) => {
    event.preventDefault();
    trackEvent(AnalyticsEvents.Navigation.NavSignUpButtonClicked);
    handleAuth('signup');
  };

  const renderLinks = () => {
    if (subscriptionBreadcrumbs) {
      return (
        <Link
          href="/"
          onPress={() =>
            trackEvent(AnalyticsEvents.Navigation.NavShortTermRentalClicked)
          }
          variant="paragraphMedium"
          testID="short-term-rental-link"
        >
          {t('navigation.short-term-rental')}
        </Link>
      );
    }
    if (!showSearchButton || !searchEnabled) {
      return (
        <>
          <Link
            openNewWindow
            href="https://team.kyte.com/driver-partner/"
            onPress={() =>
              trackEvent(AnalyticsEvents.Navigation.NavBecomeASurferClicked)
            }
            variant="paragraphMedium"
            testID="become-a-surfer-link"
          >
            {t('navigation.become-a-surfer')}
          </Link>
          <Box marginLeft="medium" style={{ cursor: 'default' }}>
            <Typography variant="paragraphMedium" color="$contentSecondary">
              |
            </Typography>
          </Box>
        </>
      );
    }
    return null;
  };

  return (
    <Header
      defaultShowSearchInput={showSearchInput}
      defaultShowSearchToggler={showSearchToggler}
      showSearchButton={showSearchButton}
      setShowSearchButton={setShowSearchButton}
      searchEnabled={searchEnabled}
      subscriptionBreadcrumbs={subscriptionBreadcrumbs}
    >
      <Logo />
      <LinksContainer>
        {renderLinks()}
        <Link
          href={FAQ_URL}
          onPress={() => trackEvent(AnalyticsEvents.Navigation.NavFaqClicked)}
          openNewWindow
          prefetch
          variant="paragraphMedium"
          testID="faq-link"
        >
          {t('navigation.faq')}
        </Link>
        {isAuthenticated ? (
          <>
            <Link
              active={router.asPath === '/account'}
              href="/account"
              onPress={() =>
                trackEvent(AnalyticsEvents.Navigation.NavAccountClicked)
              }
              prefetch
              variant="paragraphMedium"
              testID="my-account-link"
            >
              {t('navigation.my-account')}
            </Link>
            <Link
              active={router.asPath === '/invite-friends'}
              href="/invite-friends"
              onPress={() =>
                trackEvent(AnalyticsEvents.Navigation.NavReferralClicked)
              }
              prefetch
              variant="paragraphMedium"
              testID="invite-friends-link"
            >
              {t('navigation.referrals')}
            </Link>
            <Link
              active={router.asPath === '/trips'}
              href="/my-trips"
              onPress={() =>
                trackEvent(AnalyticsEvents.Navigation.NavTripsClicked)
              }
              prefetch
              variant="paragraphMedium"
              testID="my-trips-link"
            >
              {t('navigation.my-trips')}
            </Link>
            <Link
              href="/"
              onPress={handlePressSignOut}
              variant="paragraphMedium"
              testID="sign-up-link"
            >
              {t('navigation.sign-out')}
            </Link>
          </>
        ) : (
          <Link
            href={window.location.href}
            onPress={handleSignUp}
            variant="paragraphMedium"
            testID="sign-up-link"
          >
            {t('navigation.sign-up')}
          </Link>
        )}
      </LinksContainer>
    </Header>
  );
};

export default Navigation;

const LinksContainer = styled.nav`
  display: flex;
  order: 2;

  > a {
    display: block;
  }

  a:not(:first-child)  {
    margin-left: ${({ theme }) => theme.spacings.xLarge}px;
  }
}
`;
